import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addAdvancedRoiCustomization, removeAdvancedRoiCustomization, showFullRoiDetails, StoreState, undoRoiCustomizationChanges, updateRoiCustomizationName } from '../../store/reducers';
import RoiItem from './RoiItem';
import './customization-page.css';
import { generateNewId } from '../../store/model-customization-helpers';
import { selectOriginalRoiCustomizationById, selectRoiCustomizationById } from '../../store/selectors';
import { useTranslation } from 'react-i18next';

interface RoiCustomizationFormProps {
    roiId: string,
    roiIdList: string[];
}

const RoiCustomizationForm = (props: RoiCustomizationFormProps) => {
    const { roiId, roiIdList } = props;

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const roi = useSelector((state: StoreState) => selectRoiCustomizationById(state, roiId));
    const hasOriginalRoi = useSelector((state: StoreState) => selectOriginalRoiCustomizationById(state, roiId) !== undefined);

    const handleDuplicateRoi = useCallback(() => {
        if (roi?.modelCustomizationOutputId === undefined) { throw new Error('ROI has no customization output defined -- cannot duplicate'); }
        const newRoiId = generateNewId();
        dispatch(addAdvancedRoiCustomization(roi.modelCustomizationOutputId, newRoiId, roi!.id));
    }, [dispatch, roi]);

    const handleUndoStructureChanges = useCallback(() => {
        dispatch(undoRoiCustomizationChanges(roi!.id, false));
    }, [dispatch, roi]);

    const handleRevertName = useCallback(() => {
        dispatch(updateRoiCustomizationName(roi!.id, roi!.operation, false));
    }, [dispatch, roi]);

    const handleRemoveRoi = useCallback(() => {
        dispatch(removeAdvancedRoiCustomization(roi!.id));
    }, [dispatch, roi]);

    const handleShowFullRoiDetails = useCallback(() => {
        dispatch(showFullRoiDetails({ currentRoiId: roiId, roiIdList: roiIdList, isGlobalRoi: false }));
    }, [dispatch, roiId, roiIdList]);

    if (roi === undefined) {
        return null;
    }

    return (
        <RoiItem
            roiId={roiId}
            isGlobalRoi={false}
            rowActions={[
                {
                    title: t('customizeRoi.showFullRoiDetails'),
                    onClick: handleShowFullRoiDetails,
                },
                {
                    title: t('customizeRoi.duplicateAsAdvancedRoi'),
                    onClick: handleDuplicateRoi,
                },
                {
                    title: t('customizeRoi.undoChanges'),
                    onClick: handleUndoStructureChanges,
                    disabled: !hasOriginalRoi || !roi.isModified,
                }, {
                    title: roi.isBuiltInRoi ? t('customizeRoi.revertNameToDefault'): t('customizeRoi.changeNameToMatchOperation'),
                    onClick: handleRevertName,
                    disabled: roi.name === roi.operation,
                }, {
                    title: t('customizeRoi.removeRoiCustomization'),
                    onClick: handleRemoveRoi,
                    disabled: roi.isBuiltInRoi,
                    tooltip: roi.isBuiltInRoi ? t('customizeRoi.removeRoiCustomization.help.onlyAdvancedRoisCanBeRemoved') : undefined,
                },
            ]}
        />
    );
}

export default RoiCustomizationForm;
